import React, {useEffect, useMemo, useState} from "react";

import {Button, Checkbox, Col, Modal, Pagination, Row, Space} from "antd";
import {
    BoxesPaginationQuery,
    useBoxesRemoveBoxMutation,
    useGetBoxesQuery,
    useRefreshAllBoxesFmvMutation
} from "@mwServices/Boxes";
import BoxesGrid from "@components/grid/BoxesGrid";
import {PAGINATION_PAGES} from "@components/grid/Grid";
import {EditOutlined, SearchOutlined, SyncOutlined, WarningOutlined} from "@ant-design/icons";
import MyCollectionBoxListItem from "@mwPages/my-collection/boxes/MyCollectionBoxListItem";
import {ClearFiltersBar} from "@components/issues-filter-drawer";
import {BoxesEditDrawer} from "@components/boxes-edit-drawer";
import {showResponse} from "@services/Api";
import {SortIcon} from "@components/sort-icon";
import usePreferences from "@hooks/usePreferences";
import useSubscriptionType from "@hooks/useSubscriptionType";
import AssignLocationModal from "@components/assign-location-modal";

import GroupsImg from './groups.svg';
import {getWordpressUrl} from "@lib/wordpressVars";
import {InfoIcon} from "@components/icons/Icons";
import {scrollToThen} from "@lib/utils";
import {DebounceInput} from "@components/debounce-input";
import {showInfoModal} from "@lib/showModal";
import {useWindowSize} from "@components/window-size-provider";

const MyCollectionBoxes = ({search, setSearch}) => {
    const windowDimensions = useWindowSize();
    const {itemCount, setItemCount} = usePreferences();
    const defaultFilters = useMemo(() => ({
        query: '',
        sort: 0,
    }), []);
    const [queryParams, setQueryParams] = useState<BoxesPaginationQuery>({
        filters: {...defaultFilters},
        page: 1,
        perPage: itemCount,
    });
    const [postRemoveBox, removeBoxResponse] = useBoxesRemoveBoxMutation();
    const boxesQuery = useGetBoxesQuery(queryParams);
    const [postRefreshFmv, refreshFmvResponse] = useRefreshAllBoxesFmvMutation();
    const pagination = boxesQuery && boxesQuery.data && boxesQuery.data.pagination ? boxesQuery.data.pagination : null;
    const [editBox, setEditBox] = useState(null);
    const [showAssignLocationModal, setShowAssignLocationModal] = useState(false);
    const [showEditMultiple, setShowEditMultiple] = useState(false);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [showSearch, setShowSearch] = useState(false);

    const subscriptionType = useSubscriptionType();
    const allowBoxes = useMemo(() => (subscriptionType.isTrial || subscriptionType.isUnlimited), [subscriptionType]);

    useEffect( () => {
        setQueryParams({...queryParams, page: 1, filters: {...queryParams.filters, query: search}});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    },[search]);

    useEffect(() => {
        showResponse(removeBoxResponse, 'Boxes removed from collection.', 'Sorry, there was a problem removing the boxes from your collection.');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [removeBoxResponse]);

    useEffect(() => {
        setSelectedIds([]);
    }, [boxesQuery.data]);

    useEffect(() => {
        let responses = {success: 0, error: 0};
        if(refreshFmvResponse.isSuccess) {
            if(refreshFmvResponse.data.result){
                responses = refreshFmvResponse.data.result.reduce((accum, item) => {
                    return {
                        success: item.success === 1 ? accum.success+1 : accum.success,
                        error: item.success === 0 ? accum.error+1 : accum.error,
                    }
                } , responses);
            }
        }
        showResponse(refreshFmvResponse,
            ` ${responses.success} Virtual Box(es) FMV updated successfully. ${responses.error} not updated, you must wait 1 hour between updates.`,
            'Sorry, there was a problem refreshing the virtual boxes FMV.');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshFmvResponse]);

    const onGainLossInfoClick = () => {
        showInfoModal({
            title: <b>How does Gain/Loss work?</b>,
            content: (
                <>
                    <p>
                        <b>COVR</b>PRICE’s Gain/Loss calculations are available on any comic in your collection that has a purchase price entered. With a purchase price we can compare that to our calculated FMV and give you a % change and $ change allowing us to show you how well each of your purchases are performing.
                    </p>
                    <p>
                        To switch from % to $, tap the icon in the column's header. This will toggle between the two options.
                    </p>
                </>
            ),
            onOk() { },
        });
    };

    const showDesktopView = useMemo(() => {
        return windowDimensions.over(799);
    }, [windowDimensions]);


    const toggleSelected = (id: number) => {
        setSelectedIds(currentIds => {
            if(currentIds.indexOf(id) === -1) {
                return [...currentIds, id];
            } else {
                return currentIds.filter((item) => item !== id);
            }
        })
    };

    return (
        <div className="mar-5">
            {allowBoxes ? (
                <>
                    <Row className="flex-space-between">
                        <Col>
                            <Space>
                                <Button className={"greyButton " + (showEditMultiple ? ' selectedBlue' : '')} size={"large"}  onClick={() => setShowEditMultiple(!showEditMultiple)} >
                                    <span className="filterButtonText">Edit</span>
                                    <EditOutlined className="font-12"  />
                                </Button>
                            </Space>
                        </Col>
                        <Col>
                            <Space>

                                {!windowDimensions.isLargeScreen && (
                                    <Button className="greyButton " size="large" onClick={() => setShowSearch(!showSearch)}>
                                        <SearchOutlined />
                                    </Button>
                                )}

                                {pagination && (
                                    <Pagination
                                        current={pagination.current_page}
                                        pageSize={pagination.per_page}
                                        total={pagination.total_items}
                                        onChange={(page) => {
                                            if(queryParams && queryParams.page !== page){
                                                setQueryParams({...queryParams, page: page});
                                            }
                                        }}
                                        simple
                                    />
                                )}
                            </Space>
                        </Col>
                    </Row>

                    {showEditMultiple && (
                        <Row className={'edit-multiple-row ' + (windowDimensions.isLargeScreen ? 'large-screen ' : '')}>
                            <Col span={24} className="edit-multiple-col">
                                <Checkbox
                                    checked={boxesQuery.data && selectedIds.length === boxesQuery.data.boxes.length}
                                    onChange={() => {
                                        if(boxesQuery.data){
                                            if(selectedIds.length === boxesQuery.data.boxes.length){
                                                setSelectedIds([]);
                                            }else{
                                                setSelectedIds(boxesQuery.data.boxes.map(box => box.id) as number[]);
                                            }
                                        }
                                    }}
                                />

                                <Button
                                    className="greyButton mar-top-5 edit-multi-item"
                                    size={windowDimensions.isLargeScreen ? "large" : 'middle'}
                                    disabled={selectedIds.length === 0}
                                    onClick={() => { setShowAssignLocationModal(true)}}
                                >
                                    Add Location
                                </Button>

                                <Button
                                    className="greyButton mar-top-5 edit-multi-item"
                                    size={windowDimensions.isLargeScreen ? "large" : 'middle'}
                                    disabled={selectedIds.length === 0}
                                    onClick={() => Modal.confirm({
                                        autoFocusButton: null,
                                        icon: <WarningOutlined />,
                                        content: (<div>
                                            <div><b>Delete Boxes</b></div>
                                            <div className="pad-top-10" >
                                                Are you sure?<br/>
                                                Selecting this option will delete these boxes permanently. This will not delete any issues from your collection.
                                            </div>
                                        </div>),
                                        onOk() {
                                            postRemoveBox({box_ids: selectedIds.join(',')});
                                        },
                                    })}
                                >
                                    Delete Box
                                </Button>
                            </Col>

                            <AssignLocationModal
                                isModalOpen={showAssignLocationModal}
                                onCancel={() => setShowAssignLocationModal(false)}
                                selectedBoxIds={selectedIds}
                            />
                        </Row>
                    )}

                    {showSearch && (
                        <Row className="my-collection-issues-container top-line" >
                            <DebounceInput
                                placeholder={"Search Virtual Boxes..."}
                                className="searchInput"
                                prefix={<SearchOutlined />}
                                value={search}
                                maxLength={100}
                                debounceTimeout={2000}
                                onChange={(event) => {
                                    const { value } = event.target;
                                    if (setSearch && (value !== search)) setSearch(value);
                                }}
                            />
                        </Row>
                    )}

                    <ClearFiltersBar
                        filters={queryParams.filters}
                        defaultFilters={defaultFilters}
                        issueCount={pagination && pagination.total_items}
                        onClear={() => {
                            setSearch('');
                            setQueryParams({...queryParams, page: 1, filters: defaultFilters});
                        }}
                    />

                    {showDesktopView && (
                        <Row className="list-view-header">
                            {showEditMultiple && (
                                <Col span={1}>
                                </Col>
                            )}
                            <Col className="width-150">
                            </Col>
                            <Col className="pad-left-20 flex-grow-1">
                                <div className="flex">
                                    Box Title
                                    <SortIcon
                                        sort={queryParams.filters.sort}
                                        sortAsc={0}
                                        sortDesc={1}
                                        onChange={(sort) => setQueryParams({...queryParams, page: 1, filters: {...queryParams.filters, sort}})}
                                    />
                                </div>
                            </Col>
                            <Col span={4} className="no-wrap">
                                <div className="flex-align-center">
                                    # of Comics
                                    <SortIcon
                                        sort={queryParams.filters.sort}
                                        sortAsc={2}
                                        sortDesc={3}
                                        onChange={(sort) => setQueryParams({...queryParams, page: 1, filters: {...queryParams.filters, sort}})}
                                    />
                                </div>
                            </Col>
                            <Col span={4} className="no-wrap">
                                <div className="flex-align-center">
                                    Total FMV
                                    <SortIcon
                                        sort={queryParams.filters.sort}
                                        sortAsc={4}
                                        sortDesc={5}
                                        onChange={(sort) => setQueryParams({...queryParams, page: 1, filters: {...queryParams.filters, sort}})}
                                    />

                                    <Button
                                        shape={"circle"}
                                        size={"small"}
                                        className={"greyButton mar-left-5"}
                                        icon={<SyncOutlined className={'fmv-update '} />}
                                        onClick={
                                            () => {
                                                showInfoModal({
                                                    title: "Update All Virtual Box Values",
                                                    content: (<>
                                                        <p>
                                                            Selecting this option allows you to update all of your Virtual Box values at once,
                                                            however it may take over 30 seconds to process.
                                                        </p>
                                                        <p>Do you wish to proceed?</p>
                                                    </>),
                                                    onOk:  async () => {
                                                        await postRefreshFmv({});
                                                    },
                                                    okCancel: true,
                                                });
                                            }}
                                    />

                                    <Button
                                        className="info-icon"
                                        type="link"
                                        icon={<InfoIcon className={`gain-loss-icon`} />}
                                        onClick={
                                            () => {
                                                showInfoModal({
                                                    title: "Total FMV",
                                                    content: (<>
                                                        <p>
                                                            The Total FMV column allows you to know what each of the Virtual Boxes in your&nbsp;
                                                            <b>COVR</b>PRICE collection are worth, based on the individual FMVs for each comic
                                                            you've added to that Virtual Box.
                                                        </p>
                                                        <p>
                                                            You can either update each Virtual Box individually (by selecting the refresh
                                                            button next to each Virtual Box FMV)  or all at once (by selecting the refresh
                                                            icon in the column header).
                                                        </p>
                                                    </>),
                                                });
                                            }}
                                    />
                                </div>
                            </Col>
                            <Col span={4} className="no-wrap">
                                <div className="flex-align-center">Actions</div>
                            </Col>
                        </Row>
                    )}

                    <BoxesGrid
                        isLoading={boxesQuery.isLoading || boxesQuery.isFetching}
                        data={boxesQuery.data}
                        noDataMessage="Use the + Add Button Above To Add A Virtual Box to Your Collection"
                        BoxRender={(props) => (<MyCollectionBoxListItem
                            {...props}
                            setEditBox={setEditBox}
                            toggleSelected={toggleSelected}
                            onGainLossInfoClick={onGainLossInfoClick}
                        />)}
                        getItemProps={(item) => ({
                            key: item.id,
                            showEditMultiple,
                            showDesktopView,
                            isSelected: selectedIds.indexOf(item.id) !== -1,
                        })}
                        paginationType={PAGINATION_PAGES}
                        onPaginationChange={(page => {
                            if(queryParams && queryParams.page !== page){
                                scrollToThen({top: 0, behavior: 'smooth'}, () => {
                                    setQueryParams({...queryParams, page: page});
                                });
                            }
                        })}
                        onPaginationSizeChange={(current: number, perPage: number) => {
                            if(queryParams && queryParams.perPage !== perPage){
                                scrollToThen({top: 0, behavior: 'smooth'}, () => {
                                    setQueryParams({
                                        ...queryParams,
                                        page: 1,
                                        perPage: perPage,
                                    });
                                    setItemCount(perPage);
                                });
                            }
                        }}
                    />

                    <BoxesEditDrawer visible={editBox !== null} box={editBox} onClose={() => {setEditBox(null)}} />
                </>
            ) : (
                <div className="flex-align-center">
                    <Row>
                        <Col span={7} className="flex-vertical-center flex-align-center">
                            <img src={getWordpressUrl(GroupsImg)} alt="Boxes groups" />
                        </Col>
                        <Col span={1}>
                            <div className="vertical-line"></div>
                        </Col>
                        <Col span={16} className="text-left">
                            <p className="bold">Group comics from your collection into sub-collections that you can manage and track.</p>
                            <p>To gain access please upgrade to our Unlimited Membership.</p>
                            <Button className="blueButtonSquare" href="/plans/unlimited/">UPGRADE</Button>
                        </Col>
                    </Row>
                </div>
            )}
        </div>
    );
};

export default MyCollectionBoxes;